import { useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import {
  ChakraDivider,
  ChakraHStack,
  ChakraVStack,
  FAIconName,
  FireflyButton,
  ModalV2,
} from '@equip.health/ui';
import CareTeamMemberCard from '~/components/maud/patients/profile/tabs/careplan/CareTeamMemberCard';
import { CareTeamTranslations } from '~/lib/constants/careTeam.constants';
import { partitionCareTeamMembers } from '~/lib/util/careTeam.util';
import { CareTeamMemberCardProps } from '~/lib/types/careTeam/careTeam';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { PatientEvents } from '~/analytics/patientEvents';
import ProviderUniversalSelect from '~/components/common/calendar/ProviderUniversalSelect';

const TOP_LEVEL_ID = 'edit-care-team-modal';

interface EditCareTeamModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  careTeamMembers: CareTeamMemberCardProps[];
  initialCareTeamMembers: CareTeamMemberCardProps[];
  isLoading: boolean;
  setCareTeamMembers: React.Dispatch<
    React.SetStateAction<CareTeamMemberCardProps[]>
  >;
  hasTreatmentEpisode?: boolean;
  patientId?: string;
}

const modalArgs = {
  bodyStyle: {
    overflow: 'visible',
  },
  footerStyle: {
    padding: '0px',
  },
  titleStyle: {
    flex: '1 0 0',
    padding: '0px',
  },
};

const EditCareTeamModal = ({
  isOpen,
  onCancel,
  onConfirm,
  careTeamMembers,
  initialCareTeamMembers,
  isLoading,
  setCareTeamMembers,
  hasTreatmentEpisode = false,
  patientId,
}: EditCareTeamModalProps) => {
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (careTeamMembers?.length > 0) setCareTeamMembers(careTeamMembers);

    const hasEndOrVoidAssignment = careTeamMembers?.some(
      (m) => m.isEndAssignment || m.isVoidAssignment,
    );
    if (hasEndOrVoidAssignment) {
      setIsChanged(true);
    }
  }, []);
  const handleAddCareTeam = (value: ProviderUniversalSelectOption) => {
    const item: CareTeamMemberCardProps = {
      avatarSrc: value.avatar?.thumbnailUrl || value?.avatar?.url,
      firstName: value.displayFirstName || value.firstName,
      id: value.userId,
      lastName: value.displayLastName || value.lastName,
      outOfOffice: value.outOfOffice,
      role: value.providerTypeDetails?.name,
      userStatus: value.userStatus,
    };
    const newArray = [...careTeamMembers];
    if (newArray.findIndex((i) => i.id === item.id) === -1) {
      newArray.push(item);
      setIsChanged(true);
      setCareTeamMembers(newArray);
    }
  };
  const removeCareTeamMember = (userId: string) => {
    const newArr = careTeamMembers.filter((i) => i.id !== userId);
    if (newArr.length < careTeamMembers.length) {
      setCareTeamMembers(newArr);
      setIsChanged(true);
    }
  };

  const handleMemberUpdate = (
    userId: string,
    key: 'isEndAssignment' | 'isTemporary' | 'isVoidAssignment',
  ) => {
    const updatedMembers = careTeamMembers.map((member) =>
      member.id === userId ? { ...member, [key]: !member[key] } : member,
    );
    setCareTeamMembers(updatedMembers);
    setIsChanged(!isEqual(updatedMembers, initialCareTeamMembers));
  };

  const handleTrackEvent = () => {
    trackEvent(PatientEvents.providerAssignmentSaveChangesButtonClicked, {
      patientId,
      source: AnalyticsSourceValues.CARE_PLAN_TAB,
    });
  };

  const { initialMembers, newMembers, endedOrVoidMembers } =
    partitionCareTeamMembers(careTeamMembers, initialCareTeamMembers);
  const hasExistingCareTeamMembers = initialCareTeamMembers.length > 0;
  return (
    <ModalV2
      footerContent={
        <ChakraHStack justifyContent="space-between" width="100%">
          <FireflyButton
            id={`${TOP_LEVEL_ID}__cancel`}
            leftIcon={FAIconName.timesRegular}
            onClick={() => {
              trackEvent(PatientEvents.providerAssignmentCancelButtonClicked, {
                patientId,
                source: AnalyticsSourceValues.CARE_PLAN_TAB,
              });
              onCancel();
            }}
            variant="secondary"
          >
            {CareTeamTranslations.CANCEL}
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__confirm`}
            isDisabled={!isChanged || isLoading}
            leftIcon={FAIconName.checkRegular}
            onClick={() => {
              trackEvent(
                PatientEvents.providerAssignmentSaveChangesButtonClicked,
                {
                  patientId,
                  source: AnalyticsSourceValues.CARE_PLAN_TAB,
                },
              );
              onConfirm();
            }}
          >
            {CareTeamTranslations.SAVE_CHANGES}
          </FireflyButton>
        </ChakraHStack>
      }
      {...modalArgs}
      id={`${TOP_LEVEL_ID}__modal`}
      isOpen={isOpen}
      onClose={onCancel}
      size="3xl"
      title={
        hasExistingCareTeamMembers
          ? CareTeamTranslations.EDIT_PROVIDER_TEAM
          : CareTeamTranslations.ADD_PROVIDER_TEAM
      }
    >
      <ChakraVStack gap={careTeamMembers.length > 0 && '24px'} width="100%">
        <div style={{ width: '100%' }}>
          <ProviderUniversalSelect
            customPlaceholder={CareTeamTranslations.SEARCH_INPUT_PLACEHOLDER}
            handleSelect={handleAddCareTeam}
            id={TOP_LEVEL_ID}
            modalId={`${TOP_LEVEL_ID}__universal-select-modal`}
            onTrackEvent={handleTrackEvent}
            selectedProvider={null}
            trackEvent
          />
        </div>
        <ChakraVStack gap="12px" maxHeight="384px" width="100%">
          {newMembers.map((member) => (
            <CareTeamMemberCard
              id={`${TOP_LEVEL_ID}__care-team-member-card__${member.id}`}
              key={member.id}
              {...member}
              hasTreatmentEpisode={hasTreatmentEpisode}
              isEditMode
              onIsTemporaryChange={() =>
                handleMemberUpdate(member.id, 'isTemporary')
              }
              onRemove={() => removeCareTeamMember(member.id)}
            />
          ))}

          {newMembers?.length > 0 && initialCareTeamMembers?.length > 0 && (
            <ChakraDivider
              colorScheme="line"
              data-testid={`${TOP_LEVEL_ID}__divider`}
              id={`${TOP_LEVEL_ID}__divider`}
              marginBottom="12px"
              paddingTop="12px"
            />
          )}

          {initialMembers?.map((member) => (
            <CareTeamMemberCard
              hasTreatmentEpisode={hasTreatmentEpisode}
              id={`${TOP_LEVEL_ID}__care-team-member-card__${member.id}`}
              key={member.id}
              {...member}
              handleEndAssignmentChange={() =>
                handleMemberUpdate(member.id, 'isEndAssignment')
              }
              handleVoidAssignmentChange={() =>
                handleMemberUpdate(member.id, 'isVoidAssignment')
              }
              isEditMode
              onIsTemporaryChange={() =>
                handleMemberUpdate(member.id, 'isTemporary')
              }
            />
          ))}

          {endedOrVoidMembers?.map((member) => (
            <CareTeamMemberCard
              hasTreatmentEpisode={hasTreatmentEpisode}
              id={`${TOP_LEVEL_ID}__care-team-member-card__${member.id}`}
              key={member.id}
              {...member}
              handleEndAssignmentChange={() =>
                handleMemberUpdate(member.id, 'isEndAssignment')
              }
              handleVoidAssignmentChange={() =>
                handleMemberUpdate(member.id, 'isVoidAssignment')
              }
              isEndAssignment={member.isEndAssignment}
              isVoidAssignment={member.isVoidAssignment}
            />
          ))}
        </ChakraVStack>
      </ChakraVStack>
    </ModalV2>
  );
};

export default EditCareTeamModal;
